import { Link, useLocation } from 'react-router-dom';
import {
  AppWindow,
  ChevronDown,
  Church,
  Dumbbell,
  GraduationCap,
  Mic2,
  MonitorPlay,
  Presentation,
  Radio,
  RadioTower,
  Store,
  Timer,
  Users,
  Video,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  MARKETING_NAV_DROPDOWN_LINKS,
  type MarketingNavIconTone,
} from '@/content/marketingNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type MarketingSiteNavActive = 'features' | 'pricing';

type MarketingSiteNavLinksProps = {
  active?: MarketingSiteNavActive;
};

const NAV_DROPDOWN_ICONS: Record<string, typeof Timer> = {
  '/use-cases/conferences': Mic2,
  '/use-cases/presentations': Presentation,
  '/use-cases/timer-event-production': Video,
  '/use-cases/meeting-timer': Users,
  '/use-cases/online-timer-for-education': GraduationCap,
  '/use-cases/stream-timer': Radio,
  '/use-cases/broadcast-timer': RadioTower,
  '/use-cases/workout-timer': Dumbbell,
  '/use-cases/trade-show-timer': Store,
  '/use-cases/webinar-timer': MonitorPlay,
  '/use-cases/church-timer': Church,
  '/guides/timer-rezhimy-sekundomer-i-chasy': Timer,
  '/guides/timer-vmesto-vidzheta-na-sajte': AppWindow,
};

const NAV_DROPDOWN_PATHS = MARKETING_NAV_DROPDOWN_LINKS.map(({ to }) => to);

function iconToneClass(tone: MarketingNavIconTone) {
  return `cf-nav-dropdown-item-icon--${tone}`;
}

function MarketingNavDropdownItem({
  to,
  label,
  description,
  iconTone,
}: (typeof MARKETING_NAV_DROPDOWN_LINKS)[number]) {
  const Icon = NAV_DROPDOWN_ICONS[to] ?? Timer;

  return (
    <DropdownMenuItem
      className="cf-nav-dropdown-item !items-start !gap-3.5 !rounded-xl !px-3 !py-2.5 hover:!bg-[#f2f4f6] data-[highlighted]:!bg-[#f2f4f6] hover:!text-[#191c1e] data-[highlighted]:!text-[#191c1e]"
      render={<Link to={to} />}
      nativeButton={false}
    >
      <span className={cn('cf-nav-dropdown-item-icon', iconToneClass(iconTone))} aria-hidden>
        <Icon size={20} strokeWidth={2} />
      </span>
      <span className="cf-nav-dropdown-item-copy">
        <span className="cf-nav-dropdown-item-title">{label}</span>
        <span className="cf-nav-dropdown-item-desc">{description}</span>
      </span>
    </DropdownMenuItem>
  );
}

function MarketingResourcesNavDropdown({ pathname }: { pathname: string }) {
  const isActive = (NAV_DROPDOWN_PATHS as readonly string[]).includes(pathname);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn('cf-nav-dropdown-trigger', isActive && 'cf-nav-anchor--active')}
        aria-label="Сценарии и материалы"
      >
        Ресурсы
        <ChevronDown size={14} strokeWidth={2} aria-hidden className="cf-nav-dropdown-chevron" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        side="bottom"
        sideOffset={28}
        collisionPadding={{ top: 70 }}
        className="cf-nav-dropdown-panel !w-auto min-w-[34rem] max-w-[52rem] !bg-white !p-2"
      >
        {MARKETING_NAV_DROPDOWN_LINKS.map((item) => (
          <MarketingNavDropdownItem key={item.to} {...item} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Возможности, цены и выпадающее меню ресурсов в cf-nav. */
export function MarketingSiteNavLinks({ active }: MarketingSiteNavLinksProps) {
  const { pathname } = useLocation();

  return (
    <>
      {active === 'features' ? (
        <span className="cf-nav-anchor cf-nav-anchor--active" aria-current="page">
          Возможности
        </span>
      ) : (
        <Link to="/features" className="cf-nav-anchor">
          Возможности
        </Link>
      )}
      {active === 'pricing' ? (
        <span className="cf-nav-anchor cf-nav-anchor--active" aria-current="page">
          Цены
        </span>
      ) : (
        <Link to="/pricing" className="cf-nav-anchor">
          Цены
        </Link>
      )}
      <MarketingResourcesNavDropdown pathname={pathname} />
    </>
  );
}
